import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Wraps any subtree with a transparent overlay + spinner while a fetch
 * is in flight, instead of flashing empty/zero state.
 *
 *   <LoadingOverlay loading={loadingBrand}>
 *     <BrandSettingsForm data={brandData} />
 *   </LoadingOverlay>
 *
 * The wrapped subtree stays mounted (so it keeps any local edits) but
 * is blurred and pointer-events:none'd so the spinner reads as the
 * active state. Used in places where a full-page redirect would be
 * worse than a soft "this card is loading" state.
 */
export default function LoadingOverlay({ loading, children, label, minHeight, sx }) {
  return (
    <Box sx={{ position: 'relative', minHeight, ...sx }}>
      <Box
        sx={{
          transition: 'filter 120ms ease, opacity 120ms ease',
          filter: loading ? 'blur(2px)' : 'none',
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            zIndex: 1,
          }}
        >
          <CircularProgress size={32} thickness={4} />
          {label && (
            <Typography variant='caption' color='text.secondary'>
              {label}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
